import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';
import moment from 'moment';

import { InputWithConfirm, RadioOptionsWithConfirm, IQuestion, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';

import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import ExclusionOrders from './exclusionOrders';
import { IExclusionOrder } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { visaPrepFlags } from '../../../../../utilities/pages';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormAddHistoryPage4 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE2_4));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);
  const {
    hasBeenToUk,
    hasNINumber,
    niNumber,
    hasReceivedMedicalTreatment,
    hasVoluntarilyDeparted,
    hasExclusionOrder,
    exclusionOrders = []
  } = student.visa;

  const { hasTravelledToUk } = visaPrepFlags(student.personalInfo, student.history, student.offer);

  // constants
  const currentStep = 4;
  const steps = 8;
  const sectionTitle = 'Additional History';

  // component state
  const [complete, setComplete] = useState(false);
  const [exclusionOrdersState, setExclusionOrdersState] = useState<IExclusionOrder[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setExclusionOrdersState([
      ...exclusionOrders,
      {
        exclusionDate: '',
        reason: '',
        reference: ''
      }
    ]);
    setShowModal([...exclusionOrders.map(_ => false), false]);
  }, [exclusionOrders]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteOrder = (index: number): void => {
    onChange(VQ.EXCLUSION_ORDERS, [...exclusionOrders.slice(0, index), ...exclusionOrders.slice(index + 1)]);
  };

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasTravelledToUk,
      hasBeenToUk,
      hasNINumber,
      niNumber,
      hasReceivedMedicalTreatment,
      hasVoluntarilyDeparted,
      hasExclusionOrder,
      exclusionOrders
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasTravelledToUk,
    hasBeenToUk,
    hasNINumber,
    niNumber,
    hasReceivedMedicalTreatment,
    hasVoluntarilyDeparted,
    hasExclusionOrder,
    exclusionOrders
  ]);

  const onHasBeenToUkClick = (question: IQuestion) => {
    onChange(VQ.HAS_BEEN_TO_UK, question.label === 'Yes', {
      hasTravelledToUk
    });
  };

  const onHasNINumberClick = (question: IQuestion) => {
    onChange(VQ.HAS_NI_NUMBER, question.label === 'Yes');
  };

  const onHasReceivedMedicalTreatmentClick = (question: IQuestion) => {
    onChange(VQ.HAS_RECEIVED_MEDICAL_TREATMENT, question.label === 'Yes');
  };

  const onHasVoluntarilyDepartedClick = (question: IQuestion) => {
    onChange(VQ.HAS_VOLUNTARILY_DEPARTED, question.label === 'Yes');
  };

  const onHasExclusionOrderClick = (question: IQuestion) => {
    onChange(VQ.HAS_EXCLUSION_ORDER, question.label === 'Yes');
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={VQ.HAS_BEEN_TO_UK}
        id={VQ.HAS_BEEN_TO_UK}
        label={'Have you ever been to the UK?'}
        onClick={(question: any) => onHasBeenToUkClick(question)}
        isInline={true}
        maxQuestions={2}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasBeenToUk === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasBeenToUk === false
          }
        ]}
      />
      {hasBeenToUk !== undefined && (hasBeenToUk === true || hasTravelledToUk === true) ? (
        <>
          <RadioOptionsWithConfirm
            key={VQ.HAS_NI_NUMBER}
            id={VQ.HAS_NI_NUMBER}
            label={'Do you have a UK National Insurance Number?'}
            onClick={(question: any) => onHasNINumberClick(question)}
            isInline={true}
            maxQuestions={2}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasNINumber === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasNINumber === false
              }
            ]}
          />
          {hasNINumber === true ? (
            <InputWithConfirm
              key={VQ.NI_NUMBER}
              id={VQ.NI_NUMBER}
              value={niNumber}
              label={'What is the number?'}
              onChange={(evt: any) => {
                onChange(VQ.NI_NUMBER, evt.currentTarget.value);
              }}
            />
          ) : null}
          <RadioOptionsWithConfirm
            key={VQ.HAS_RECEIVED_MEDICAL_TREATMENT}
            id={VQ.HAS_RECEIVED_MEDICAL_TREATMENT}
            label={'Have you ever received medical treatment in the UK?'}
            onClick={(question: any) => onHasReceivedMedicalTreatmentClick(question)}
            isInline={true}
            maxQuestions={2}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasReceivedMedicalTreatment === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasReceivedMedicalTreatment === false
              }
            ]}
          />
          <RadioOptionsWithConfirm
            key={VQ.HAS_VOLUNTARILY_DEPARTED}
            id={VQ.HAS_VOLUNTARILY_DEPARTED}
            label={'Have you ever voluntarily elected to depart the UK?'}
            onClick={(question: any) => onHasVoluntarilyDepartedClick(question)}
            isInline={true}
            maxQuestions={2}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasVoluntarilyDeparted === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasVoluntarilyDeparted === false
              }
            ]}
          />
          <RadioOptionsWithConfirm
            key={VQ.HAS_EXCLUSION_ORDER}
            id={VQ.HAS_EXCLUSION_ORDER}
            label={'Have you received, or have you been subject to, an exclusion order from the UK?'}
            onClick={(question: any) => onHasExclusionOrderClick(question)}
            isInline={true}
            maxQuestions={2}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasExclusionOrder === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasExclusionOrder === false
              }
            ]}
          />
          {hasExclusionOrder === true ? (
            <CardContainer title={`How many exclusion orders have you received?`}>
              {exclusionOrdersState.map((order: IExclusionOrder, index: number) => (
                <CardWithModal
                  key={index}
                  id={`exclusion-order-${index}`}
                  index={index}
                  modalTitle={'Add Exclusion Order'}
                  cardItems={[
                    {
                      label: 'Date of exclusion',
                      value: moment(order.exclusionDate).format('DD MMM YYYY')
                    },
                    {
                      label: 'Reason',
                      value: order.reason
                    },
                    {
                      label: 'Reference',
                      value: order.reference
                    }
                  ]}
                  onRemove={deleteOrder}
                  addButtonLabel={`Add Exclusion Order`}
                  addAnotherButtonLabel={`I have another Exclusion Order to add`}
                  hasAddMoreButton={order.exclusionDate === '' && order.reason === '' && order.reference === ''}
                  showModal={showModal[index]}
                  toggleModal={toggleModal}
                >
                  <ExclusionOrders
                    index={index}
                    exclusionDate={order.exclusionDate}
                    reason={order.reason}
                    reference={order.reference}
                    onSaveAndClose={(order: IExclusionOrder) => {
                      const orders = [...exclusionOrders];
                      orders[index] = {
                        ...order
                      };
                      onChange(VQ.EXCLUSION_ORDERS, orders);
                      toggleModal(index);
                    }}
                  />
                </CardWithModal>
              ))}
            </CardContainer>
          ) : null}
        </>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage4;
